import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "forealco_cookie_consent";

/**
 * Cookie consent banner.
 *  - Shows on first visit (when no value in localStorage)
 *  - Three actions: Accept All · Reject Non-Essential · Manage Preferences
 *  - "Manage Preferences" routes to the Cookie Policy section of /legal
 *  - Re-openable via the global "forealco:open-cookie-preferences" event
 */
export default function CookieBanner() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      if (!v) setOpen(true);
    } catch {
      setOpen(true);
    }
    const reopen = () => setOpen(true);
    window.addEventListener("forealco:open-cookie-preferences", reopen);
    return () =>
      window.removeEventListener("forealco:open-cookie-preferences", reopen);
  }, []);

  const persist = useCallback((value) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ value, ts: new Date().toISOString() })
      );
    } catch {
      /* ignore storage errors */
    }
    setOpen(false);
  }, []);

  if (!open) return null;

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-label="Cookie preferences"
      className="fixed z-[60] bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-[440px]"
    >
      <div
        className="border border-white/15 shadow-2xl"
        style={{
          background: "rgba(11, 14, 19, 0.96)",
          backdropFilter: "blur(14px)",
        }}
      >
        <div className="px-5 pt-5 pb-4 flex items-start gap-3">
          <div className="shrink-0 w-9 h-9 border border-[#9DB4FF]/40 flex items-center justify-center text-[#9DB4FF]">
            <Cookie size={16} strokeWidth={1.6} />
          </div>
          <div className="flex-1">
            <div className="font-mono text-[10px] tracking-[0.35em] uppercase text-[#9DB4FF]">
              COOKIES
            </div>
            <p className="mt-2 text-[12.5px] leading-[1.7] text-white/80">
              We use cookies to improve site functionality, analyse traffic, and
              understand how visitors use Forealco. You can accept all cookies,
              reject non-essential cookies, or manage your preferences.
            </p>
          </div>
          <button
            type="button"
            data-testid="cookie-banner-close"
            aria-label="Dismiss"
            onClick={() => persist("essential")}
            className="shrink-0 text-white/40 hover:text-white transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-5 pb-5 grid grid-cols-1 sm:grid-cols-3 gap-2">
          <button
            type="button"
            data-testid="cookie-accept-all"
            onClick={() => persist("all")}
            className="text-[10px] tracking-[0.3em] uppercase px-3 py-2.5 bg-[#9DB4FF] text-[#0E1116] hover:bg-white transition-colors"
          >
            Accept All
          </button>
          <button
            type="button"
            data-testid="cookie-reject"
            onClick={() => persist("essential")}
            className="text-[10px] tracking-[0.3em] uppercase px-3 py-2.5 border border-white/20 text-white/85 hover:border-white/60 transition-colors"
          >
            Reject Non-Essential
          </button>
          <Link
            to="/legal#cookies"
            data-testid="cookie-manage"
            onClick={() => persist("managed")}
            className="text-[10px] tracking-[0.3em] uppercase px-3 py-2.5 border border-[#9DB4FF]/40 text-[#9DB4FF] hover:bg-[#9DB4FF]/10 transition-colors text-center"
          >
            Manage Preferences
          </Link>
        </div>
      </div>
    </div>
  );
}
